// Automated Publishing Service with Cron Jobs
import { supabase } from '@/config/supabase';
import { socialMediaAPI } from './platformAPIs';

interface ScheduledPost {
  id: string;
  content: string;
  platforms: string[];
  scheduled_time: string;
  status: 'pending' | 'published' | 'failed';
  image_url?: string;
  hashtags?: string[];
  retry_count: number;
  error_message?: string;
  published_ids?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

interface PublishingQueue {
  post_id: string;
  platform: string;
  scheduled_time: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  attempts: number;
  last_attempt?: string;
  error?: string;
}

export class AutoPublishingService {
  private checkInterval: number = 60000; // Check every minute
  private intervalId: NodeJS.Timeout | null = null;
  private isProcessing: boolean = false;

  // Start the cron service
  start() {
    if (this.intervalId) {
      console.log('Auto-publishing service already running');
      return;
    }

    console.log('Starting auto-publishing service...');
    
    // Check immediately on start
    this.checkAndPublishPosts();
    
    // Then check every minute
    this.intervalId = setInterval(() => {
      this.checkAndPublishPosts();
    }, this.checkInterval);

    // Also set up real-time subscription for immediate posts
    this.setupRealtimeSubscription();
  }

  // Stop the cron service
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('Auto-publishing service stopped');
    }
  }

  // Setup real-time subscription for immediate publishing
  private setupRealtimeSubscription() {
    supabase
      .channel('scheduled_posts_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'scheduled_posts',
          filter: 'status=eq.pending'
        },
        (payload) => {
          console.log('New scheduled post detected:', payload.new);
          this.processPost(payload.new as ScheduledPost);
        }
      )
      .subscribe();
  }

  // Main cron job function
  private async checkAndPublishPosts() {
    if (this.isProcessing) {
      console.log('Already processing posts, skipping this cycle');
      return;
    }

    this.isProcessing = true;

    try {
      const now = new Date();
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60000);

      // Get posts scheduled for the next 5 minutes
      const { data: posts, error } = await supabase
        .from('scheduled_posts')
        .select('*')
        .eq('status', 'pending')
        .lte('scheduled_time', fiveMinutesFromNow.toISOString())
        .order('scheduled_time', { ascending: true });

      if (error) {
        console.error('Error fetching scheduled posts:', error);
        return;
      }

      if (!posts || posts.length === 0) {
        console.log('No posts to publish at this time');
        return;
      }

      console.log(`Found ${posts.length} posts to process`);

      // Process each post
      for (const post of posts) {
        await this.processPost(post);
      }
    } catch (error) {
      console.error('Error in checkAndPublishPosts:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  // Process a single post
  private async processPost(post: ScheduledPost) {
    const scheduledTime = new Date(post.scheduled_time);
    const now = new Date();

    // If it's not time yet, skip
    if (scheduledTime > now) {
      console.log(`Post ${post.id} scheduled for ${scheduledTime}, waiting...`);
      return;
    }

    console.log(`Processing post ${post.id} for platforms:`, post.platforms);

    try {
      // Update status to processing
      await supabase
        .from('scheduled_posts')
        .update({ 
          status: 'processing',
          updated_at: new Date().toISOString()
        })
        .eq('id', post.id);

      // Publish to all platforms
      const results = await socialMediaAPI.publishToAll(
        post.content,
        post.platforms,
        post.image_url,
        scheduledTime
      );

      // Check if all platforms succeeded
      const allSucceeded = Object.values(results).every(r => r.success);
      const someSucceeded = Object.values(results).some(r => r.success);

      // Collect successful post IDs
      const publishedIds: Record<string, string> = {};
      const failedPlatforms: string[] = [];

      for (const [platform, result] of Object.entries(results)) {
        if (result.success && result.id) {
          publishedIds[platform] = result.id;
          
          // Add to publishing queue for tracking
          await this.addToQueue(post.id, platform, 'completed');
        } else {
          failedPlatforms.push(platform);
          await this.addToQueue(post.id, platform, 'failed', result.error);
        }
      }

      // Update post status
      if (allSucceeded) {
        await supabase
          .from('scheduled_posts')
          .update({
            status: 'published',
            published_ids: publishedIds,
            updated_at: new Date().toISOString()
          })
          .eq('id', post.id);

        console.log(`Post ${post.id} successfully published to all platforms`);
        
        // Send success notification
        await this.sendNotification('success', post, publishedIds);
      } else if (someSucceeded) {
        // Partial success - update with retry for failed platforms
        const newRetryCount = (post.retry_count || 0) + 1;
        
        await supabase
          .from('scheduled_posts')
          .update({
            status: newRetryCount >= 3 ? 'failed' : 'pending',
            platforms: failedPlatforms, // Only retry failed platforms
            published_ids: publishedIds,
            retry_count: newRetryCount,
            error_message: `Failed on: ${failedPlatforms.join(', ')}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', post.id);

        console.log(`Post ${post.id} partially published. Failed platforms:`, failedPlatforms);
      } else {
        // Complete failure
        const newRetryCount = (post.retry_count || 0) + 1;
        
        await supabase
          .from('scheduled_posts')
          .update({
            status: newRetryCount >= 3 ? 'failed' : 'pending',
            retry_count: newRetryCount,
            error_message: 'Failed to publish to all platforms',
            scheduled_time: new Date(now.getTime() + 10 * 60000).toISOString(), // Retry in 10 minutes
            updated_at: new Date().toISOString()
          })
          .eq('id', post.id);

        console.log(`Post ${post.id} failed to publish. Retry count: ${newRetryCount}`);
        
        // Send failure notification after max retries
        if (newRetryCount >= 3) {
          await this.sendNotification('failure', post);
        }
      }

      // Collect and save analytics
      if (Object.keys(publishedIds).length > 0) {
        await this.scheduleAnalyticsCollection(post.id, publishedIds);
      }

    } catch (error) {
      console.error(`Error processing post ${post.id}:`, error);
      
      await supabase
        .from('scheduled_posts')
        .update({
          status: 'failed',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          updated_at: new Date().toISOString()
        })
        .eq('id', post.id);
    }
  }

  // Add to publishing queue for tracking
  private async addToQueue(
    postId: string,
    platform: string,
    status: 'completed' | 'failed',
    error?: string
  ) {
    await supabase
      .from('publishing_queue')
      .insert({
        post_id: postId,
        platform,
        status,
        attempts: 1,
        last_attempt: new Date().toISOString(),
        error
      });
  }

  // Schedule analytics collection
  private async scheduleAnalyticsCollection(
    postId: string,
    publishedIds: Record<string, string>
  ) {
    // Schedule analytics collection for 1 hour, 24 hours, and 7 days after publishing
    const delays = [
      { delay: 60 * 60 * 1000, label: '1h' },           // 1 hour
      { delay: 24 * 60 * 60 * 1000, label: '24h' },     // 24 hours
      { delay: 7 * 24 * 60 * 60 * 1000, label: '7d' }   // 7 days
    ];

    for (const { delay, label } of delays) {
      const collectionTime = new Date(Date.now() + delay);
      
      await supabase
        .from('analytics_collection_queue')
        .insert({
          post_id: postId,
          published_ids: publishedIds,
          collection_time: collectionTime.toISOString(),
          collection_label: label,
          status: 'pending'
        });
    }
  }

  // Send notifications
  private async sendNotification(
    type: 'success' | 'failure',
    post: ScheduledPost,
    publishedIds?: Record<string, string>
  ) {
    // You can implement email, push notifications, or in-app notifications here
    const notification = {
      type,
      title: type === 'success' 
        ? '✅ Post opublikowany pomyślnie'
        : '❌ Błąd publikacji posta',
      message: type === 'success'
        ? `Post został opublikowany na platformach: ${post.platforms.join(', ')}`
        : `Nie udało się opublikować posta. Sprawdź ustawienia połączeń.`,
      post_id: post.id,
      published_ids: publishedIds,
      created_at: new Date().toISOString()
    };

    await supabase
      .from('notifications')
      .insert(notification);

    // If you have a notification service, trigger it here
    // notificationService.send(notification);
  }

  // Manually trigger publishing for a specific post
  async publishNow(postId: string): Promise<boolean> {
    try {
      const { data: post, error } = await supabase
        .from('scheduled_posts')
        .select('*')
        .eq('id', postId)
        .single();

      if (error || !post) {
        console.error('Post not found:', error);
        return false;
      }

      await this.processPost(post);
      return true;
    } catch (error) {
      console.error('Error in publishNow:', error);
      return false;
    }
  }

  // Get publishing queue status
  async getQueueStatus(): Promise<{
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  }> {
    const { data, error } = await supabase
      .from('publishing_queue')
      .select('status');

    if (error || !data) {
      return { pending: 0, processing: 0, completed: 0, failed: 0 };
    }

    const status = {
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0
    };

    data.forEach(item => {
      const s = item.status as keyof typeof status;
      if (s in status) {
        status[s]++;
      }
    });

    return status;
  }

  // Clean up old completed items from queue
  async cleanupQueue(daysToKeep: number = 7) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

    const { error } = await supabase
      .from('publishing_queue')
      .delete()
      .eq('status', 'completed')
      .lt('last_attempt', cutoffDate.toISOString());

    if (error) {
      console.error('Error cleaning up queue:', error);
    } else {
      console.log('Queue cleanup completed');
    }
  }
}

// Create singleton instance
export const autoPublishingService = new AutoPublishingService();

// Schedule a post for publishing
export async function schedulePost(
  content: string,
  platforms: string[],
  scheduledTime: Date,
  imageUrl?: string,
  hashtags?: string[]
): Promise<{ success: boolean; postId?: string; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('scheduled_posts')
      .insert({
        content,
        platforms,
        scheduled_time: scheduledTime.toISOString(),
        status: 'pending',
        image_url: imageUrl,
        hashtags,
        retry_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, postId: data.id };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

// Get scheduled posts
export async function getScheduledPosts(
  status?: 'pending' | 'published' | 'failed'
): Promise<ScheduledPost[]> {
  let query = supabase
    .from('scheduled_posts')
    .select('*')
    .order('scheduled_time', { ascending: true });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching scheduled posts:', error);
    return [];
  }

  return data || [];
}

// Cancel a scheduled post
export async function cancelScheduledPost(postId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('scheduled_posts')
      .update({
        status: 'cancelled',
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .eq('status', 'pending'); // Only cancel pending posts

    return !error;
  } catch (error) {
    console.error('Error cancelling post:', error);
    return false;
  }
}

// Update scheduled post
export async function updateScheduledPost(
  postId: string,
  updates: Partial<ScheduledPost>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('scheduled_posts')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', postId);

    return !error;
  } catch (error) {
    console.error('Error updating scheduled post:', error);
    return false;
  }
}
